import React, { useState, useCallback } from 'react';
import { Button, Card, FileUpload, ErrorAlert } from '../../../components/shared';
import { cn } from '../../../utils/cn';
import FaceCapture from './FaceCapture';

const MODES = [
  { value: 'upload', label: 'Tải ảnh lên' },
  { value: 'camera', label: 'Chụp ảnh' },
];

export default function FaceMatchStep({ onSubmit, loading, error }) {
  const [mode, setMode] = useState('upload');
  const [selfie, setSelfie] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleFile = useCallback((file) => {
    setSelfie(file);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
  }, [preview]);

  const handleReset = useCallback(() => {
    setSelfie(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
  }, [preview]);

  if (mode === 'camera' && !selfie) {
    return (
      <FaceCapture
        onCapture={handleFile}
        onCancel={() => setMode('upload')}
      />
    );
  }

  return (
    <Card title="Xác thực khuôn mặt" description="Chụp ảnh selfie rõ ràng, nhìn thẳng vào camera">
      <ErrorAlert error={error} className="mb-4" />

      <div className="mb-4 flex rounded-lg bg-gray-100 p-1">
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => { handleReset(); setMode(m.value); }}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              mode === m.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="mx-auto max-w-sm">
        {!selfie && mode === 'upload' && (
          <FileUpload
            label="Ảnh selfie"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
            disabled={loading}
          />
        )}

        {!selfie && mode === 'camera' && (
          <button
            type="button"
            onClick={() => { setSelfie(null); setPreview(null); setMode('camera'); }}
            className="flex w-full flex-col items-center gap-3 rounded-lg border-2 border-dashed border-gray-300 p-8 text-gray-400 transition-colors hover:border-blue-400 hover:text-blue-500"
          >
            <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
            </svg>
            <span className="text-sm font-medium">Mở camera để chụp</span>
          </button>
        )}

        {selfie && preview && (
          <div className="flex flex-col items-center gap-3">
            <img src={preview} alt="Selfie" className="max-h-60 rounded-lg object-contain" />
            <button
              type="button"
              onClick={handleReset}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {mode === 'camera' ? 'Chụp lại' : 'Chọn ảnh khác'}
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={() => onSubmit(selfie)} loading={loading} disabled={!selfie}>
          Tiếp tục
        </Button>
      </div>
    </Card>
  );
}
